import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { USE_CASES, LanguageContent } from '../data/useCases';
import SeoHead from '../components/SeoHead';
import { BreadcrumbSchema } from '../components/JsonLd';

const UseCases: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const lang = t('common.lang', { defaultValue: 'fr' }) as keyof LanguageContent;

  return (
    <>
      <SeoHead
        title={lang === 'fr'
          ? "Cas d'usages | TotalEnergies, Hydro-Québec, FrAIdom — Colibri Technologies"
          : 'Use Cases | TotalEnergies, Hydro-Québec, FrAIdom — Colibri Technologies'}
        description={lang === 'fr'
          ? "Decouvrez comment Colibri Technologies a accompagne TotalEnergies, Hydro-Quebec, FrAIdom et d'autres leaders dans leurs projets IoT, integration digitale et IA."
          : 'Discover how Colibri Technologies supported TotalEnergies, Hydro-Quebec, FrAIdom and other leaders in their IoT, digital integration and AI projects.'}
        canonical="/use-cases"
      />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://colibritechnologies.ca/' },
        { name: lang === 'fr' ? "Cas d'usages" : 'Use Cases', url: 'https://colibritechnologies.ca/use-cases' },
      ]} />
    <div className="max-w-7xl mx-auto px-6 py-8">
      {/* Heading */}
      <div className="text-center mb-14">
        <h1
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 700,
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            color: 'var(--color-text-primary)',
          }}
        >
          {t('usecases.title', lang === 'fr' ? 'Cas d\'usages' : 'Use Cases')}
        </h1>
        <div
          className="mx-auto mt-4"
          style={{ width: 60, height: 3, background: 'var(--gradient-primary)', borderRadius: 2 }}
        />
      </div>

      <div className="space-y-6">
        {USE_CASES.map((uc) => (
          <div
            key={uc.slug}
            className="card-glass overflow-hidden"
            style={{ borderRadius: 'var(--radius-xl)', cursor: 'pointer', border: '1px solid rgba(255,255,255,0.05)', transition: 'border-color 250ms, box-shadow 250ms' }}
            onClick={() => navigate('/use-cases/' + uc.slug)}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = `${uc.sectorColor}40`; (e.currentTarget as HTMLElement).style.boxShadow = `0 0 0 1px ${uc.sectorColor}20, 0 16px 48px rgba(0,0,0,0.5)`; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.05)'; (e.currentTarget as HTMLElement).style.boxShadow = ''; }}
          >
            <div className="md:flex">
              {/* Logo panel */}
              <div
                className="md:w-1/5 p-8 flex flex-col items-center justify-center gap-4"
                style={{
                  borderRight: '1px solid rgba(255,255,255,0.05)',
                  background: 'rgba(255,255,255,0.015)',
                  minWidth: 180,
                }}
              >
                <img
                  src={uc.logo}
                  alt={lang === 'fr'
                    ? `Logo ${uc.company} — projet Colibri Technologies`
                    : `${uc.company} logo — Colibri Technologies project`}
                  width={160}
                  height={56}
                  className="max-w-[140px] h-auto"
                  style={uc.logoFilter !== false
                    ? { filter: 'brightness(0) invert(1)', opacity: 0.85 }
                    : { opacity: 1, maxHeight: 48 }}
                />
                <span
                  className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold text-center leading-tight"
                  style={{
                    background: `${uc.sectorColor}18`,
                    color: uc.sectorColor,
                    border: `1px solid ${uc.sectorColor}35`,
                    fontFamily: 'var(--font-body)',
                  }}
                >
                  {uc.sector[lang]}
                </span>
              </div>

              {/* Content panel */}
              <div className="flex-1 p-7 flex flex-col">
                <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
                  <h2
                    style={{
                      fontFamily: 'var(--font-display)',
                      fontWeight: 700,
                      fontSize: '1.375rem',
                      color: uc.sectorColor,
                    }}
                  >
                    {uc.company}
                  </h2>
                  <span style={{ fontFamily: 'var(--font-body)', fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>
                    {uc.duration[lang]}
                    {uc.ongoing && (
                      <span
                        className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs"
                        style={{ background: 'rgba(16,185,129,0.1)', color: '#10B981' }}
                      >
                        <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#10B981', display: 'inline-block' }} />
                        {lang === 'fr' ? 'En cours' : 'Ongoing'}
                      </span>
                    )}
                  </span>
                </div>

                <h3
                  className="mb-3"
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontWeight: 600,
                    fontSize: '1.0625rem',
                    color: 'var(--color-text-primary)',
                  }}
                >
                  {uc.title[lang]}
                </h3>

                <p
                  className="mb-5"
                  style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.9375rem',
                    lineHeight: 1.65,
                    color: 'var(--color-text-secondary)',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {uc.context[lang]}
                </p>

                {/* Key results (top 2) */}
                <div className="flex flex-wrap gap-3 mb-5">
                  {uc.results.slice(0, 2).map((r, i) => (
                    <div
                      key={i}
                      className="flex items-baseline gap-1.5 px-3 py-2 rounded-lg"
                      style={{ background: `${uc.sectorColor}0f`, border: `1px solid ${uc.sectorColor}28` }}
                    >
                      <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '1.125rem', color: uc.sectorColor }}>{r.value}</span>
                      <span style={{ fontFamily: 'var(--font-body)', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{r.label[lang]}</span>
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap items-center gap-2 mt-auto">
                  {uc.technologies.slice(0, 5).map((tech) => (
                    <span key={tech} className="badge badge-primary">{tech}</span>
                  ))}
                  {uc.technologies.length > 5 && (
                    <span style={{ fontFamily: 'var(--font-body)', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>+{uc.technologies.length - 5}</span>
                  )}
                  <span
                    className="ml-auto flex items-center gap-1.5 text-sm font-medium"
                    style={{ color: uc.sectorColor, fontFamily: 'var(--font-body)' }}
                  >
                    {lang === 'fr' ? 'Voir le cas d\'usage' : 'View use case'}
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M3 7h8M8 4l3 3-3 3" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* CTA */}
      <div
        className="mt-14 text-center card-glass py-12 px-8"
        style={{ borderRadius: 'var(--radius-2xl)' }}
      >
        <h3
          className="mb-3"
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 700,
            fontSize: '1.5rem',
            color: 'var(--color-text-primary)',
          }}
        >
          {lang === 'fr' ? 'Un projet similaire en tête ?' : 'Have a similar project in mind?'}
        </h3>
        <p
          className="mb-6"
          style={{ fontFamily: 'var(--font-body)', color: 'var(--color-text-secondary)' }}
        >
          {lang === 'fr'
            ? 'Nous sommes là pour vous aider à concrétiser votre vision.'
            : "We're here to help you bring your vision to life."}
        </p>
        <button
          className="btn-primary"
          onClick={() => navigate('/')}
        >
          {lang === 'fr' ? 'Discutons-en' : "Let's Talk"}
        </button>
      </div>
    </div>
    </>
  );
};

export default UseCases;
